import Box from '@mui/material/Box';
import { ConsentCheckbox } from '@/Pages/ParentalConsent/components/ConsentCheckbox';
import { GuardianFormField } from '@/Pages/ParentalConsent/components/GuardianFormField';
import { RelationshipSelect } from '@/Pages/ParentalConsent/components/RelationshipSelect';
import { parentalConsentCopy } from '@/Pages/ParentalConsent/copy';
import { applyCpfMask } from '@/Pages/Signup/utils/cpfMask';
import { applyPhoneMask } from '@/Pages/ParentalConsent/utils/phoneMask';
import type { GuardianRelationship } from '@/types/parental-consent';
import type { ParentalConsentFormData } from '@/types/parental-consent';
import '@/Pages/ParentalConsent/ParentalConsentPage.sass';

type GuardianFormProps = {
  form: ParentalConsentFormData;
  fieldErrors: Partial<Record<keyof ParentalConsentFormData, string>>;
  touched: Partial<Record<keyof ParentalConsentFormData, boolean>>;
  setField: (field: keyof ParentalConsentFormData, value: string | boolean) => void;
  blurField: (field: keyof ParentalConsentFormData) => void;
};

export function GuardianForm({
  form,
  fieldErrors,
  touched,
  setField,
  blurField,
}: GuardianFormProps): JSX.Element {
  return (
    <Box
      component="form"
      className="guardian-form"
      data-testid="guardian-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <GuardianFormField
        id="guardianFullName"
        label={parentalConsentCopy.guardianFullNameLabel}
        placeholder={parentalConsentCopy.guardianFullNamePlaceholder}
        value={form.guardianFullName}
        error={fieldErrors.guardianFullName}
        showError={Boolean(touched.guardianFullName)}
        onChange={(value) => setField('guardianFullName', value)}
        onBlur={() => blurField('guardianFullName')}
        autoComplete="name"
      />

      <GuardianFormField
        id="guardianEmail"
        label={parentalConsentCopy.guardianEmailLabel}
        placeholder={parentalConsentCopy.guardianEmailPlaceholder}
        type="email"
        value={form.guardianEmail}
        error={fieldErrors.guardianEmail}
        showError={Boolean(touched.guardianEmail)}
        onChange={(value) => setField('guardianEmail', value)}
        onBlur={() => blurField('guardianEmail')}
        inputMode="email"
        autoComplete="email"
      />

      <GuardianFormField
        id="guardianCpf"
        label={parentalConsentCopy.guardianCpfLabel}
        placeholder={parentalConsentCopy.guardianCpfPlaceholder}
        value={form.guardianCpf}
        error={fieldErrors.guardianCpf}
        showError={Boolean(touched.guardianCpf)}
        onChange={(value) => setField('guardianCpf', applyCpfMask(value))}
        onBlur={() => blurField('guardianCpf')}
        inputMode="numeric"
        autoComplete="off"
      />

      <GuardianFormField
        id="guardianPhone"
        label={parentalConsentCopy.guardianPhoneLabel}
        placeholder={parentalConsentCopy.guardianPhonePlaceholder}
        value={form.guardianPhone}
        error={fieldErrors.guardianPhone}
        showError={Boolean(touched.guardianPhone)}
        onChange={(value) => setField('guardianPhone', applyPhoneMask(value))}
        onBlur={() => blurField('guardianPhone')}
        inputMode="tel"
        autoComplete="tel"
      />

      <RelationshipSelect
        value={form.guardianRelationship}
        error={fieldErrors.guardianRelationship}
        showError={Boolean(touched.guardianRelationship)}
        onChange={(value: GuardianRelationship) => setField('guardianRelationship', value)}
        onBlur={() => blurField('guardianRelationship')}
      />

      <ConsentCheckbox
        checked={form.consentGiven}
        error={fieldErrors.consentGiven}
        showError={Boolean(touched.consentGiven)}
        onChange={(checked) => setField('consentGiven', checked)}
        onBlur={() => blurField('consentGiven')}
      />
    </Box>
  );
}
